/**
 * Reserve Cart Image Updater
 * Displays original product images for deposit items in cart
 */

class ReserveCartImageUpdater {
  constructor() {
    this.processedItems = new Set();
    this.init();
  }

  init() {
    // Update images when page loads
    setTimeout(() => this.updateCartImages(), 500);
    setTimeout(() => this.updateCartImages(), 1500);
    setTimeout(() => this.updateCartImages(), 3000);

    // Update images when cart is refreshed
    document.addEventListener('cart:refresh', () => {
      this.processedItems.clear();
      setTimeout(() => this.updateCartImages(), 300);
    });

    document.addEventListener('cart:item-added', (event) => {
      this.processedItems.clear();
      setTimeout(() => this.updateCartImages(), 500);
      setTimeout(() => this.updateCartImages(), 1500);
    });

    // Subscribe to cart update events (when items are added/removed/changed via cart.js)
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        console.log('📦 Cart updated, refreshing deposit images...');
        this.processedItems.clear();
        // Wait for DOM to be updated, then update images multiple times for reliability
        setTimeout(() => this.updateCartImages(), 200);
        setTimeout(() => this.updateCartImages(), 500);
        setTimeout(() => this.updateCartImages(), 1000);
      });
    }

    // Listen for cart drawer opening
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver(() => {
        setTimeout(() => this.updateCartImages(), 200);
      });
      observer.observe(cartDrawer, { childList: true, subtree: true });
    }
  }

  async updateCartImages() {
    try {
      // Get current cart data
      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (!cart || !cart.items || cart.items.length === 0) {
        return;
      }

      console.log('🔍 Checking cart for deposit items...', cart.items.length, 'items');

      // Find deposit items
      const depositItems = cart.items.filter(item =>
        item.product_title && item.product_title.includes('Reserved Deposit')
      );

      if (depositItems.length === 0) {
        console.log('No deposit items found in cart');
        return;
      }

      console.log('Found', depositItems.length, 'deposit item(s)');

      // Process each cart item to find deposits and track their actual index
      cart.items.forEach((item, cartIndex) => {
        // Check if this is a deposit item
        if (!item.product_title || !item.product_title.includes('Reserved Deposit')) {
          return;
        }

        const imageUrl = item.properties && item.properties._product_image;
        const productUrl = item.properties && item.properties._product_url;
        const reservedFor = item.properties && item.properties['Reserved for'];

        if (imageUrl && reservedFor) {
          console.log(`Processing deposit item at cart index ${cartIndex}:`, {
            reservedFor,
            imageUrl: imageUrl.substring(0, 50) + '...',
            productUrl: productUrl ? productUrl.substring(0, 50) + '...' : 'none',
            variantId: item.variant_id
          });

          // Use the actual cart index (plus 1 for Shopify's 1-based indexing)
          this.replaceImageBySearch(cartIndex + 1, imageUrl, productUrl, reservedFor, item.variant_id);
        } else {
          console.warn('Missing image URL or reserved product name for deposit item');
        }
      });
    } catch (error) {
      console.error('Reserve Cart Image Update Error:', error);
    }
  }

  replaceImageBySearch(cartPosition, imageUrl, productUrl, reservedFor, variantId) {
    // Create a unique key for this item using cart position and variant
    const itemKey = `${cartPosition}-${variantId}-${imageUrl}`;
    if (this.processedItems.has(itemKey)) {
      console.log(`⏭️  Skipping already processed item at position ${cartPosition}`);
      return; // Already processed
    }

    // Try multiple strategies to find the cart item
    let cartItem = null;

    // Strategy 1: Try by ID with cart position (most reliable)
    cartItem = document.getElementById(`CartDrawer-Item-${cartPosition}`);
    if (cartItem) {
      console.log(`✅ Found by ID: CartDrawer-Item-${cartPosition}`);
    }

    // Strategy 2: Try main cart page structure
    if (!cartItem) {
      cartItem = document.getElementById(`CartItem-${cartPosition}`);
      if (cartItem) {
        console.log(`✅ Found by ID: CartItem-${cartPosition}`);
      }
    }

    // Strategy 3: Search by matching "Reserved for" text (more precise than just "Reserved Deposit")
    if (!cartItem) {
      const allCartItems = document.querySelectorAll('tr.cart-item, .cart-item, [class*="cart-item"]');
      console.log(`⚠️  Fallback: Searching through ${allCartItems.length} cart items by text content...`);

      for (const item of allCartItems) {
        const text = item.textContent || '';
        // Match both "Reserved Deposit" and the specific product name
        if (text.includes('Reserved Deposit') && text.includes(reservedFor)) {
          // Additional check: make sure we haven't already processed this DOM element
          const alreadyProcessed = Array.from(this.processedItems).some(key =>
            item.getAttribute('data-processed-key') === key
          );
          if (!alreadyProcessed) {
            cartItem = item;
            cartItem.setAttribute('data-processed-key', itemKey);
            console.log(`✅ Found cart item by matching "Reserved for: ${reservedFor}"`);
            break;
          }
        }
      }
    }

    if (!cartItem) {
      console.warn(`❌ Could not find cart item for position ${cartPosition}`);
      return;
    }

    console.log(`Cart item element:`, cartItem.tagName, cartItem.id || cartItem.className);

    // Find the media container (the td or div that should contain the image)
    let mediaContainer = cartItem.querySelector('td.cart-item__media, .cart-item__media');

    if (!mediaContainer) {
      console.warn('❌ Could not find cart-item__media container');
      return;
    }

    console.log('Found media container:', mediaContainer.tagName, mediaContainer.className);

    // Try to find existing image element
    let imgElement = mediaContainer.querySelector('img');

    // If no image exists, CREATE it!
    if (!imgElement) {
      console.log('📝 No image element found - creating new one');

      // Create the link wrapper
      const link = document.createElement('a');
      link.href = productUrl || '#';
      link.className = 'cart-item__link';
      link.setAttribute('aria-hidden', 'false');

      // Create the image
      imgElement = document.createElement('img');
      imgElement.className = 'cart-item__image';
      imgElement.loading = 'lazy';
      imgElement.width = 150;

      // Append image to link, and link to container
      link.appendChild(imgElement);
      mediaContainer.appendChild(link);

      console.log('✅ Created new image element');
    } else {
      console.log('Found existing image element:', imgElement.className);
    }

    // Set the image source
    console.log('🔄 Setting image to:', imageUrl);

    imgElement.src = imageUrl;
    imgElement.srcset = imageUrl;
    imgElement.alt = reservedFor;

    // Update the link if it exists
    const imgLink = imgElement.closest('a');
    if (imgLink && productUrl) {
      imgLink.href = productUrl;
      imgLink.setAttribute('aria-hidden', 'false');
      imgLink.removeAttribute('tabindex');
      console.log('Updated image link to:', productUrl.substring(0, 50) + '...');
    }

    // Mark as processed
    this.processedItems.add(itemKey);
    console.log('✅ Successfully updated cart image for:', reservedFor);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ReserveCartImageUpdater());
} else {
  new ReserveCartImageUpdater();
}
